import { useCallback, useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { AbasSublinhadas, PageHeader } from "@/components/padroes";
import { BeneficiosFixosCard } from "@/components/beneficios/BeneficiosFixosCard";
import { BonificacaoConfigCard } from "@/components/bonificacao/BonificacaoConfigCard";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { centsToInput, formatCents, parseReaisInput } from "@/lib/format";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Parametro {
  chave: string;
  valor: string;
  tipo: string;
  descricao: string | null;
}

const FRANQUIA_AMIL = "amil.franquiaTitular";

type Aba = "beneficios" | "bonificacao";

const RHConfiguracoes = () => {
  const [aba, setAba] = useState<Aba>("beneficios");
  const [parametros, setParametros] = useState<Parametro[]>([]);
  const [franquia, setFranquia] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const nomeBonificacao = useNomeBonificacao();

  const load = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await api.get<Parametro[]>("/parametros");
      setParametros(lista);
      const atual = lista.find((p) => p.chave === FRANQUIA_AMIL);
      if (atual) setFranquia(centsToInput(Number(atual.valor)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar parâmetros");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const salvarFranquia = async () => {
    let reais: number;
    try {
      reais = parseReaisInput(franquia);
    } catch {
      toast.error("Valor inválido. Use apenas números, ex: 100,00");
      return;
    }
    if (reais < 0) {
      toast.error("A franquia não pode ser negativa");
      return;
    }

    setSalvando(true);
    try {
      await api.patch(`/parametros/${FRANQUIA_AMIL}`, {
        valor: String(Math.round(reais * 100)),
      });
      toast.success("Franquia atualizada");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  const atual = parametros.find((p) => p.chave === FRANQUIA_AMIL);

  return (
    <RoleGuard role="rh">
      <AppLayout title="Configurações">
        <div className="mx-auto flex max-w-[1040px] flex-col gap-4">
          <PageHeader
            titulo="Configurações"
            subtitulo="Regras usadas nos cálculos da folha, dos benefícios e das bonificações."
          />

          <AbasSublinhadas
            value={aba}
            onChange={setAba}
            abas={[
              { value: "beneficios", label: "Benefícios" },
              { value: "bonificacao", label: nomeBonificacao },
            ]}
          />

          {aba === "beneficios" && (
            <div className="flex flex-col gap-4">
              <section className="flex flex-wrap rounded-xl border bg-card">
                <div className="flex flex-[1_1_300px] flex-col gap-1.5 border-border-soft px-6 py-[22px] min-[640px]:border-r">
                  <h3 className="text-[15px] font-semibold">Franquia da coparticipação Amil</h3>
                  <p className="text-[13px] leading-relaxed text-muted-foreground">
                    Valor abatido das despesas do <strong className="text-foreground">titular</strong>, uma vez por
                    colaborador por competência. Despesas de dependentes são repassadas integralmente e não consomem
                    a franquia.
                  </p>
                </div>
                <div className="flex flex-[1_1_300px] flex-col gap-2.5 px-6 py-[22px]">
                  {carregando ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="franquia">Valor da franquia (R$)</Label>
                        <div className="flex gap-2">
                          <Input
                            id="franquia"
                            value={franquia}
                            onChange={(e) => setFranquia(e.target.value)}
                            inputMode="decimal"
                            placeholder="100,00"
                            className="max-w-[180px]"
                          />
                          <Button onClick={() => void salvarFranquia()} disabled={salvando} className="h-10">
                            {salvando && <Loader2 className="animate-spin" />}
                            Salvar
                          </Button>
                        </div>
                      </div>

                      {atual && (
                        <p className="text-xs leading-normal text-muted-foreground">
                          Valor em uso:{" "}
                          <strong className="font-semibold text-foreground">{formatCents(Number(atual.valor))}</strong>.
                          Competências já criadas mantêm a franquia vigente no momento da abertura: mudar aqui só
                          afeta as próximas.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </section>

              <BeneficiosFixosCard />
            </div>
          )}

          {aba === "bonificacao" && <BonificacaoConfigCard />}
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHConfiguracoes;
